<template>
  <Transition name="mission-toast">
    <output
      v-if="visible"
      class="mission-toast"
    >
      <CoinToken size="md" />
      <div class="copy">
        <p class="title">{{ headline }}</p>
        <p class="hint">{{ t('missions.toastHint') }}</p>
      </div>
      <button
        type="button"
        class="cta"
        @click="goClaim"
      >
        {{ t('missions.claimCta') }}
      </button>
    </output>
  </Transition>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCollectionStore } from '../stores/collection'
import { useMissionsStore } from '../stores/missions'
import CoinToken from './CoinToken.vue'
import { useI18n } from '../i18n/t'

const TOAST_MS = 5_000

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const missions = useMissionsStore()
const collection = useCollectionStore()
let hideTimer: ReturnType<typeof setTimeout> | null = null

const ids = computed(() => missions.claimToastIds)
const rankedStep = computed(() => missions.rankedToastStep)
const onMissionsPage = computed(() => route.name === 'daily-missions')
const held = computed(() => collection.packCeremony)
const visible = computed(() =>
  (ids.value.length > 0 || rankedStep.value !== null) &&
  !onMissionsPage.value &&
  !held.value,
)

const headline = computed(() => {
  const list = ids.value
  const step = rankedStep.value
  if (step && list.length === 0) {
    return t('missions.toastRanked', { n: step })
  }
  if (step && list.length > 0) {
    return t('missions.toastDoneMany', { n: list.length + 1 })
  }
  const only = list.length === 1 ? list[0] : undefined
  if (only) {
    return t('missions.toastDone', { name: t(`missions.ids.${only}`) })
  }
  return t('missions.toastDoneMany', { n: list.length })
})

function clearTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

function goClaim(): void {
  const rankedOnly = rankedStep.value !== null && ids.value.length === 0
  missions.dismissClaimToast()
  void router.push(rankedOnly
    ? { name: 'daily-missions', query: { tab: 'ranked' } }
    : { name: 'daily-missions' })
}

watch(
  () => [ids.value.join(','), rankedStep.value, onMissionsPage.value, held.value] as const,
  ([, step, onPage, paused]) => {
    clearTimer()
    if (paused) return
    if (onPage && (ids.value.length > 0 || step !== null)) {
      missions.dismissClaimToast()
      return
    }
    if (!visible.value) return
    hideTimer = setTimeout(() => {
      missions.dismissClaimToast()
    }, TOAST_MS)
  },
  { immediate: true },
)

onUnmounted(() => {
  clearTimer()
})
</script>

<style scoped>
.mission-toast {
  position: fixed;
  left: 50%;
  bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  z-index: 10050;
  display: flex;
  align-items: center;
  gap: 12px;
  width: min(520px, calc(100vw - 32px));
  padding: 12px 12px 12px 16px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container-high);
  color: var(--md-sys-color-on-surface);
  box-shadow: var(--md-elevation-2);
  transform: translateX(-50%);
  font-family: Roboto, system-ui, sans-serif;
}

.copy {
  min-width: 0;
  flex: 1 1 auto;
}

.title,
.hint {
  margin: 0;
}

.title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.hint {
  margin-top: 2px;
  color: var(--md-sys-color-on-surface-variant);
  font-size: 12px;
  line-height: 16px;
}

.cta {
  flex: 0 0 auto;
  padding: 8px 14px;
  border: 0;
  border-radius: 999px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.cta:hover,
.cta:focus-visible {
  outline: none;
  filter: brightness(1.06);
}

.mission-toast-enter-active,
.mission-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.mission-toast-enter-from,
.mission-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .mission-toast-enter-active,
  .mission-toast-leave-active {
    transition: none;
  }
}
</style>
